import React, { useEffect, useMemo } from "react";
import { ThreeEvent, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { createExtrudeGeometryFromShape } from "../geometry";
import SelectedMeshOutline from "./SelectedMeshOutline";

const ExtrudedShapeMesh: React.FC<{
  shape: THREE.Shape;
  depth: number;
  positionZ?: number;
  castShadow?: boolean;
  receiveShadow?: boolean;
  selected?: boolean;
  userData?: Record<string, unknown>;
  raycast?: THREE.Object3D["raycast"];
  configureGeometry?: (
    geometry: THREE.ExtrudeGeometry,
    positionZ: number,
  ) => void;
  onPointerDown?: (event: ThreeEvent<PointerEvent>) => void;
  onPointerMove?: (event: ThreeEvent<PointerEvent>) => void;
  onPointerUp?: (event: ThreeEvent<PointerEvent>) => void;
  onPointerCancel?: (event: ThreeEvent<PointerEvent>) => void;
  children: React.ReactNode;
}> = ({
  shape,
  depth,
  positionZ = 0,
  castShadow = true,
  receiveShadow = true,
  selected = false,
  userData,
  raycast,
  configureGeometry,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  children,
}) => {
  const { gl } = useThree();
  const geometry = useMemo(() => {
    const extrudeGeometry = createExtrudeGeometryFromShape(shape, depth);
    configureGeometry?.(extrudeGeometry, positionZ);
    return extrudeGeometry;
  }, [configureGeometry, depth, positionZ, shape]);

  useEffect(() => () => geometry.dispose(), [geometry]);

  return (
    <mesh
      geometry={geometry}
      position={[0, 0, positionZ]}
      castShadow={castShadow}
      receiveShadow={receiveShadow}
      userData={userData}
      raycast={raycast}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onPointerOver={() => {
        gl.domElement.style.cursor = onPointerDown ? "pointer" : "default";
      }}
      onPointerOut={() => {
        gl.domElement.style.cursor = "default";
      }}
    >
      {children}
      <SelectedMeshOutline visible={selected} />
    </mesh>
  );
};

export default ExtrudedShapeMesh;
